import * as yup from 'yup';
import { CurrentDistributionOrderDTO } from '../../../AccountDistributionOrderModal/api/useAccountDistributionOptionsQuery';
import { AssumptionsDTO } from '../FirmSettings/api/useFirmSettingsQuery';
import { assumptionsValidation } from '../FirmSettings/components/Assumptions/Assumptions.meta';
import { validation as agreementsValidation } from '../FirmSettings/components/AutoAccountsAgreements/AutoAccountsAgreements.meta';
import { FormAgreement } from '../FirmSettings/FirmSettings.meta';
import { MySettingsDTO } from './api/useMySettingsQuery';

export const validation = yup.object().shape({
  email: yup
    .string()
    .email('This must be a valid email')
    .required('This field is required'),

  firstName: yup.string().required('This field is required'),

  calendarUrl: yup
    .string()
    .url('This must be a valid URL')
    .nullable(),

  agreements: agreementsValidation,
  adviserAssumptions: assumptionsValidation,
});

export interface FormValues {
  adviserImageFilename: string | null;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  calendarUrl: string;
  customSignature: string;
  adviserAssumptions: AssumptionsDTO;
  defaultPortfolioGroupId: string | null;
  allowAssetBucketingFirm: boolean;
  allowAssetBucketingAdviser: boolean;
  allowClientToCustomizeAssetAssignmentAcrossBuckets: boolean;
  allowBeFi20Adviser: boolean;
  allowPulseCheckAdviser: boolean;
  allowMobileAppPushNotifications: boolean;
  defaultDocumentUploadMobilePushNotifications: boolean;
  emailDocumentNotificationsAsOwner: boolean;
  emailDocumentNotificationsAsAdvisor: boolean;
  accountDistributionOrder: CurrentDistributionOrderDTO;

  // Conditional fields
  agreements: FormAgreement[];
  adviserFirmLogoFilename?: string | null;
  adviserCustomDisclosures?: string;
  customTermsAndConditions?: string;
  allowAddManualAccount?: boolean;
}

export const parseDefaultValues = (settings: MySettingsDTO): FormValues => ({
  adviserImageFilename: settings.adviserImageFilename,
  firstName: settings.firstName || '',
  lastName: settings.lastName || '',
  email: settings.email || '',
  phoneNumber: settings.phoneNumber || '',
  calendarUrl: settings.calendarUrl || '',
  customSignature: settings.customSignature || '',
  adviserAssumptions: settings.adviserAssumptions,
  defaultPortfolioGroupId: settings.defaultPortfolioGroupId,
  adviserFirmLogoFilename: settings.adviserFirmLogoFilename,
  adviserCustomDisclosures: settings.adviserCustomDisclosures || '',
  customTermsAndConditions: settings.customTermsAndConditions || '',
  agreements: settings.agreements?.map(({ name, isActive }) => ({
    name,
    isActive,
  })),
  allowAssetBucketingFirm: settings.allowAssetBucketingFirm,
  allowAssetBucketingAdviser: settings.allowAssetBucketingAdviser,
  allowClientToCustomizeAssetAssignmentAcrossBuckets:
    settings.allowClientToCustomizeAssetAssignmentAcrossBuckets,
  allowAddManualAccount: settings.allowAddManualAccount,
  allowBeFi20Adviser: settings.allowBeFi20Adviser,
  allowPulseCheckAdviser: settings.allowPulseCheckAdviser,
  allowMobileAppPushNotifications: settings.allowMobileAppPushNotifications,
  defaultDocumentUploadMobilePushNotifications:
    settings.defaultDocumentUploadMobilePushNotifications,
  emailDocumentNotificationsAsOwner: settings.emailDocumentNotificationsAsOwner,
  emailDocumentNotificationsAsAdvisor: settings.emailDocumentNotificationsAsAdvisor,
  accountDistributionOrder: settings.accountDistributionOrder,
});

export const parseSubmittedValues = (
  values: FormValues,
  settings: MySettingsDTO,
): MySettingsDTO => {
  return {
    ...settings,
    ...values,
    agreements: (values.agreements || []).map((formAgreement, index) => ({
      ...settings.agreements[index],
      ...formAgreement,
    })),
    adviserAssumptions: {
      ...settings.adviserAssumptions,
      ...values.adviserAssumptions,
    },
  };
};

export type FormFieldName =
  | 'adviserFirmLogoFilename'
  | 'adviserCustomDisclosures'
  | 'customTermsAndConditions'
  | 'allowAddManualAccount'
  | 'mobileAppNotificationsAdviser';
